import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Person } from '@mui/icons-material';

import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { ListContentLayout } from '@frontend/features/common/components/ListContentLayout';
import { CaseStatisticsModal } from './CaseStatisticsModal';
import { Link } from '@frontend/features/common/components/Link';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { routes } from '@frontend/router/routes';
import { ChipCustom } from '@frontend/features/common/components/ChipCustom';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import { Dot } from '@frontend/features/common/components/Dot';
import { formatISOStringToFriendlyDate, getTimeSinceDate } from '@frontend/features/common/helper';
import { CaseResponseDto } from '@bandido/shared/dtos';

interface CasesListDesktopProps {
  title: string;
  subtitle: string;
  pagination: any;
  filtersCount: number;
  filtersOptions: any;
  onSearchChange: (value: string) => void;
  sorting: any;
  onCreate: () => void;
  actionToggles?: any[];
  statisticsButton: JSX.Element;
  isLoading: boolean;
  items: CaseResponseDto[];
  totalCount: number;
  isStatisticsModalOpen: boolean;
  onCloseStatistics: () => void;
}

export const CasesListDesktop = ({
  title,
  subtitle,
  pagination,
  filtersCount,
  filtersOptions,
  onSearchChange,
  sorting,
  onCreate,
  actionToggles,
  statisticsButton,
  isLoading,
  items,
  totalCount,
  isStatisticsModalOpen,
  onCloseStatistics,
}: CasesListDesktopProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Desktop specific columns
  const desktopColumns = [
    {
      id: 'id',
      label: 'id',
      align: 'left' as const,
      isSticky: true,
      canSort: true,
      minWidth: 100,
      format: (data: string) => (
        <Link underline="hover" to={routes.dashboard.caseView.replace(':id', data)}>
          {addLeadingZeros(Number(data), defaultLeadingZeros)}
        </Link>
      ),
    },
    {
      id: 'clientFullName',
      minWidth: 140,
      isSticky: true,
      canSort: true,
      sortField: 'clientFirstName',
      label: t('cases.list.table.client'),
      align: 'left' as const,
    },
    {
      id: 'subject',
      canSort: true,
      sortField: 'name',
      label: t('cases.list.table.subject'),
      align: 'left' as const,
      maxWidth: 300,
      tooltip: true,
    },
    {
      id: 'status',
      canSort: true,
      label: t('cases.list.table.status'),
      align: 'left' as const,
    },
    {
      id: 'step',
      canSort: true,
      label: t('cases.list.table.step'),
      align: 'left' as const,
    },
    {
      id: 'agent',
      canSort: true,
      sortField: 'agentFirstName',
      label: t('cases.list.table.agent'),
      align: 'left' as const,
    },

    {
      id: 'team',
      canSort: true,
      sortField: 'teamName',
      label: t('cases.list.table.team'),
      align: 'left' as const,
      maxWidth: 300,
      tooltip: true,
    },
    {
      id: 'workflow',
      sortField: 'workflowName',
      canSort: true,
      label: t('cases.list.table.workflow'),
      align: 'left' as const,
      maxWidth: 300,
      tooltip: true,
    },
    {
      id: 'organization',
      canSort: true,
      sortField: 'organizationName',
      label: t('cases.list.table.organization'),
      align: 'left' as const,
    },
    {
      id: 'agencyName',
      canSort: true,
      label: t('cases.list.table.agency'),
      align: 'left' as const,
    },
    {
      id: 'channel',
      canSort: true,
      sortField: 'originatedByChannel',
      label: t('cases.list.table.channel'),
      align: 'left' as const,
    },
    {
      id: 'updatedSince',
      canSort: true,
      sortField: 'updatedAt',
      label: t('cases.list.table.lastUpdate'),
      align: 'left' as const,
    },
    {
      id: 'created',
      canSort: true,
      sortField: 'createdAt',
      label: t('cases.list.table.created'),
      align: 'left' as const,
    },
  ];

  // Transform raw data for desktop table view
  const tableData = {
    key: 'id',
    items: items.map((dbItem) => ({
      clientFullName: dbItem.clientFirstName ? (
        <Box display="flex" alignItems="center" gap={1}>
          <IconAvatar>
            <Person fontSize="small" htmlColor={theme.palette.common.white} />
          </IconAvatar>
          <Link underline="hover" to={routes.dashboard.clientView.replace(':id', String(dbItem.clientId))}>
            {`${dbItem.clientFirstName} ${dbItem.clientLastName}`}
          </Link>
        </Box>
      ) : (
        'N/A'
      ),
      id: dbItem.id,
      subject: dbItem.name,
      agencyName: dbItem.agencyName || t('common.unassigned'),
      team: dbItem.teamId ? (
        <Box display="flex" alignItems="center" gap={1}>
          <Dot color={dbItem.teamColor} />
          <Link
            underline="hover"
            to={`${routes.dashboard.agencyView.root.replace(
              ':id',
              String(dbItem.agencyId),
            )}/${routes.dashboard.agencyView.team.replace(':teamId', String(dbItem.teamId))}`}
          >
            {dbItem.teamName}
          </Link>
        </Box>
      ) : (
        t('common.unassigned')
      ),
      status: (
        <ChipCustom
          size="small"
          label={t(`cases.status.${dbItem.status}`)}
          backgroundColor={theme.palette.cases[dbItem.status].bg}
          textColor={theme.palette.cases[dbItem.status].text}
          component="span"
        />
      ),
      channel: dbItem.originatedByChannel ? t(`cases.originatedByChannel.${dbItem.originatedByChannel}`) : t('common.unassigned'),
      organization: dbItem.organizationName || '',
      workflow: dbItem.workflowName || t('common.unassigned'),
      step: dbItem.stepName || t('common.unassigned'),
      agent: dbItem.agentId ? (
        <Box display="flex" alignItems="center" gap={1}>
          <IconAvatar>
            <Person fontSize="small" htmlColor={theme.palette.common.white} />
          </IconAvatar>
          <Link underline="hover" to={routes.dashboard.userView.replace(':id', String(dbItem.agentId))}>
            {`${dbItem.agentFirstName} ${dbItem.agentLastName}`}
          </Link>
        </Box>
      ) : (
        'N/A'
      ),
      created: formatISOStringToFriendlyDate(dbItem.createdAt),
      updatedSince: getTimeSinceDate(dbItem.updatedAt),
    })),
    totalCount,
  };

  return (
    <ListLayout title={title} subtitle={subtitle}>
      <ListContentLayout
        pagination={pagination}
        filtersCount={filtersCount}
        stickyHeader
        filters={filtersOptions}
        onSearchChange={onSearchChange}
        sorting={sorting}
        onCreate={onCreate}
        actionToggles={actionToggles}
        statisticsButton={statisticsButton}
        columns={desktopColumns}
        isLoading={isLoading}
        data={tableData}
      />

      <CaseStatisticsModal 
        open={isStatisticsModalOpen} 
        onClose={onCloseStatistics} 
      />
    </ListLayout>
  );
};
